import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { ImageOff } from "lucide-react";
import { Alert, cn } from "@platform/ui";
import { useT } from "../i18n";
import { DEFAULT_TOPIC, HELP_TOPICS, helpTopic } from "../help";
import type { HelpField, HelpShot, HelpTopic } from "../help/types";
import { PageCard, TableShell, td, th } from "../layout/Page";

/** In-console user manual: /help (contents → first topic) and /help/$topic. */
export function HelpPage() {
  const { t, locale } = useT();
  const id = useParams({ strict: false }).topic ?? DEFAULT_TOPIC;
  const topic = helpTopic(id, locale);

  return (
    <div className="mx-auto flex w-full max-w-[var(--container-content)] flex-col gap-4 lg:flex-row lg:items-start">
      <nav
        aria-label={t("help.contents")}
        className="rounded-[var(--radius-card)] border border-line bg-paper p-3 lg:sticky lg:top-4 lg:w-64 lg:shrink-0"
      >
        <p className="px-2 pb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
          {t("help.contents")}
        </p>
        <ul className="flex flex-col gap-0.5">
          {HELP_TOPICS.map((p) => {
            const active = p.en.id === id;
            return (
              <li key={p.en.id}>
                <Link
                  to="/help/$topic"
                  params={{ topic: p.en.id }}
                  aria-current={active ? "page" : undefined}
                  data-testid={`help-topic-${p.en.id}`}
                  className={cn(
                    "block rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-subtle",
                    active ? "bg-primary/10 font-medium text-primary-dark" : "text-ink",
                  )}
                >
                  {p[locale].title}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <PageCard className="min-w-0 flex-1">
        {topic ? (
          <TopicView topic={topic} />
        ) : (
          <div className="p-6">
            <Alert tone="warning">{t("help.notFound")}</Alert>
          </div>
        )}
      </PageCard>
    </div>
  );
}

function TopicView({ topic }: { topic: HelpTopic }) {
  const { t, locale } = useT();
  const related = (topic.related ?? []).map((r) => helpTopic(r, locale)).filter((r): r is HelpTopic => !!r);
  return (
    <article data-testid="help-article" className="px-6 py-6">
      <h2 className="text-xl font-semibold tracking-tight">{topic.title}</h2>
      <p className="mt-2 text-sm text-ink-2">{topic.summary}</p>
      <p className="mt-3 rounded-[var(--radius-control)] bg-subtle px-3 py-2 text-sm">
        <span className="font-medium">{t("help.access")}: </span>
        {topic.access}
      </p>
      {topic.shot && <Shot shot={topic.shot} />}

      {topic.sections.length > 1 && (
        <nav aria-label={t("help.onThisPage")} className="mt-6">
          <p className="text-xs font-semibold tracking-wide text-ink-2 uppercase">{t("help.onThisPage")}</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm">
            {topic.sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-primary-dark hover:underline">
                  {s.heading}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      )}

      {topic.sections.map((s) => (
        <section key={s.id} id={s.id} className="mt-8 scroll-mt-4 border-t border-line pt-6">
          <h3 className="text-lg font-semibold">{s.heading}</h3>
          {s.body?.map((p, i) => (
            <p key={i} className="mt-3 text-sm leading-relaxed">
              {p}
            </p>
          ))}
          {s.shot && <Shot shot={s.shot} />}
          {s.fields && s.fields.length > 0 && (
            <Fields title={s.fieldsTitle ?? t("help.fields")} fields={s.fields} />
          )}
          {s.steps && s.steps.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium">{t("help.steps")}</p>
              <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm leading-relaxed">
                {s.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          )}
          {s.tips && s.tips.length > 0 && (
            <Alert tone="info" title={t("help.tips")} className="mt-4">
              <ul className="list-disc space-y-1 pl-4">
                {s.tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </Alert>
          )}
          {s.warnings && s.warnings.length > 0 && (
            <Alert tone="warning" title={t("help.warnings")} className="mt-4">
              <ul className="list-disc space-y-1 pl-4">
                {s.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </Alert>
          )}
        </section>
      ))}

      {related.length > 0 && (
        <section className="mt-8 border-t border-line pt-6">
          <h3 className="text-base font-semibold">{t("help.related")}</h3>
          <ul className="mt-2 flex flex-wrap gap-2">
            {related.map((r) => (
              <li key={r.id}>
                <Link
                  to="/help/$topic"
                  params={{ topic: r.id }}
                  className="inline-block rounded-full border border-line px-3 py-1 text-sm text-primary-dark hover:bg-subtle"
                >
                  {r.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

function Fields({ title, fields }: { title: string; fields: HelpField[] }) {
  const { t } = useT();
  return (
    <div className="mt-4 overflow-hidden rounded-[var(--radius-control)] border border-line">
      <TableShell label={title}>
        <thead>
          <tr>
            <th className={cn(th, "w-1/3")}>{title}</th>
            <th className={th}>{t("help.description")}</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.name}>
              <td className={cn(td, "align-top font-medium")}>
                {f.name}
                {f.required && (
                  <span className="ml-1.5 text-xs font-normal text-danger">{t("help.required")}</span>
                )}
              </td>
              <td className={cn(td, "align-top leading-relaxed")}>{f.description}</td>
            </tr>
          ))}
        </tbody>
      </TableShell>
    </div>
  );
}

/** A screenshot from public/help/<locale>/ — a placeholder until the screenshots are generated. */
function Shot({ shot }: { shot: HelpShot }) {
  const { t, locale } = useT();
  const [missing, setMissing] = useState(false);
  const src = `${import.meta.env.BASE_URL}help/${locale}/${shot.file}`;
  return (
    <figure className="mt-4">
      {missing ? (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-[var(--radius-control)] border border-dashed border-line-strong bg-subtle text-sm text-ink-3">
          <ImageOff className="size-6" aria-hidden />
          {t("help.noImage")}
        </div>
      ) : (
        <a href={src} target="_blank" rel="noreferrer" title={t("help.openImage")}>
          <img
            src={src}
            alt={shot.caption}
            loading="lazy"
            onError={() => setMissing(true)}
            className="w-full rounded-[var(--radius-control)] border border-line shadow-[var(--shadow-xs)]"
          />
        </a>
      )}
      <figcaption className="mt-1.5 text-center text-xs text-ink-2">{shot.caption}</figcaption>
    </figure>
  );
}
